import { NftsToBuyMoleculeReatom } from "@/app/nft/buy/nfts-to-buy/molecule.reatom"
import type { PurchasableNft } from "@/app/nft/buy/nfts-to-buy/scope"
import { getBaseNftListEventContext } from "@/app/nft/utils/get-base-nft-event-context"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import type { PriceValue } from "@anaconda/core/price/types"
import {
  type MultiBuyRequest,
  multiBuyFactoryReatom,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import type { Address } from "viem"
import { EvmSdkContainerMoleculeReatom } from "../../blockchains/evm-sdk-container-molecule.reatom"
import { BatchBuyToast } from "../../toasts/batch-buy-toast"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "../events/transaction-event"
import { invalidateBoughtNftQueries } from "../order/invalidate-bought-nft-queries"

export interface BatchBuyActionParams {
  nfts: PurchasableNft[]
  price: PriceValue | undefined
}

export interface BatchBuyActionResult {
  txId?: Address
  nfts: PurchasableNft[]
  price: PriceValue | undefined
}

export const BatchBuyTransactionMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)
  const { evmAddress: evmAddressAtom } = mol(WalletConnectMoleculeReatom)
  const { cancelNftSelection } = mol(NftsToBuyMoleculeReatom)

  const batchBuyAction = action(
    async ({ nfts, price }: BatchBuyActionParams) => {
      const container = sdkContainerAtom()
      const from = evmAddressAtom()

      if (!from) {
        throw new Error("EVM wallet address is not available")
      }

      if (nfts.length === 0) {
        throw new Error("No NFTs to buy")
      }

      const blockchain = nfts[0].collection.blockchain
      const itemIds = nfts.map((nft) => nft.id)

      // Execute batch buy via Reatom SDK
      const multiBuy = multiBuyFactoryReatom(container)
      const request: MultiBuyRequest = {
        itemIds,
        from: from as Address,
        blockchain,
      }

      const result = await wrap(multiBuy(request))

      return { txId: result.txId, nfts, price }
    },
    "batchBuyAction",
  ).extend(
    withAsync(),

    // onCall: Called when action starts (replaces onMutate)
    withCallHook(async (values, [params]) => {
      const { nfts, price } = params

      sonnerToast.custom(
        (id) => <BatchBuyToast.Pending nfts={nfts} price={price} id={id} />,
        {
          duration: Number.POSITIVE_INFINITY,
        },
      )

      const batchTransactionEventContext: EventContext = {
        flow: Flow.MultipleBuy,
      }

      logger?.api.pushEvent(
        TransactionEvent.Started,
        batchTransactionEventContext,
      )
      amplitude.track(TransactionEvent.Started, batchTransactionEventContext)
    }),
  )

  // onFulfill: Called on success (replaces onSuccess)
  batchBuyAction.onFulfill.extend(
    withCallHook((result, [params]) => {
      const { txId, nfts, price } = result.payload

      if (nfts.length <= 0 || !txId) {
        return
      }

      for (const nft of nfts) {
        invalidateBoughtNftQueries(nft)
      }

      // Clear the cart after successful purchase
      cancelNftSelection()

      const collection = nfts[0].collection
      const batchTransactionEventContext: EventContext = {
        flow: Flow.MultipleBuy,
      }

      const eventContext = {
        ...batchTransactionEventContext,
        ...getBaseNftListEventContext({
          ids: nfts.map((nft) => nft.id),
          price,
          collection,
        }),
        transactionId: txId,
      }

      logger?.api.pushEvent(TransactionEvent.Completed, eventContext)
      amplitude.track(TransactionEvent.Completed, eventContext)

      sonnerToast.dismiss()
      sonnerToast.custom((id) => (
        <BatchBuyToast.Success
          price={price}
          nfts={nfts}
          id={id}
          transactionId={txId}
        />
      ))
    }),
  )

  // onReject: Called on error (replaces onError)
  batchBuyAction.onReject.extend(
    withCallHook(({ error, params: [paramsValue] }) => {
      console.error("Batch buy failed:", error)

      const { nfts, price } = paramsValue

      const batchTransactionEventContext: EventContext = {
        flow: Flow.MultipleBuy,
      }

      const eventContext = {
        ...batchTransactionEventContext,
        error: getTransactionErrorMessage(error),
      }

      logger?.api.pushEvent(TransactionEvent.Failed, eventContext)
      amplitude.track(TransactionEvent.Failed, eventContext)

      sonnerToast.dismiss()

      if (nfts.length <= 0) {
        return
      }

      sonnerToast.custom((id) => (
        <BatchBuyToast.Error
          nfts={nfts}
          id={id}
          price={price}
          error={error as Error}
        />
      ))
    }),
  )

  return { batchBuyAction }
})
